import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useExportPat from "services/delta/pat/useExportPat";
import useAllRatsb from "services/delta/ratsb/useAllRatsb";
import useGetRole from "hooks/useGetRole";
import useDeltaStore from "stores/useDeltaStore";
import moment from "moment";
import NoData from "components/pages/noData/NoData";
import DeltaCardConBaja from "./DeltaCardConBaja";
import DeltaCardSinBaja from "./DeltaCardSinBaja";
import "./ResultadoBusquedaDeltas.scss";

type DeltaType = "withLeave" | "withoutLeave";
type FilterKey = "all" | "validados" | "noValidados" | "remesados" | "noRemesados";

interface IProps {
  arrData: any[];
  type: DeltaType;
  isRemitted?: boolean;
}

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all",         label: "Todos" },
  { key: "validados",   label: "Validados" },
  { key: "noValidados", label: "Sin validar" },
  { key: "remesados",   label: "Remesados" },
  { key: "noRemesados", label: "Sin remesar" },
];

const checkStatusPAT = (status: number): string => {
  const map: Record<number, string> = {
    0: "Nuevo", 1: "Actualizado", 2: "Verificado",
    3: "Exportado", 4: "Success", 5: "Error",
  };
  return map[status] ?? "";
};

const checkTypePAT = (t: number): string =>
  t === 1 ? "Accidente" : t === 2 ? "Recaída" : "";

const ResultadoBusquedaDeltas: React.FC<IProps> = ({ arrData, type, isRemitted = false }) => {
  const navigate = useNavigate();
  const { isImpersonation } = useGetRole;
  const { exportPat } = useExportPat();
  const { exportRatsb } = useAllRatsb();

  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [arrayRemesa, setArrayRemesa] = useState<string[]>([]);
  const [formatedData, setFormatedData] = useState<any[]>([]);

  const patFilterValues      = useDeltaStore((s) => s.patFilterValues);
  const ratsbFilterValues    = useDeltaStore((s) => s.ratsbFilterValues);

  // Determina qué filtros pill ocultar según lo ya filtrado en el formulario
  const appliedFormValues = useMemo(() => {
    if (isRemitted) return null;
    return type === "withLeave" ? patFilterValues : ratsbFilterValues;
  }, [type, isRemitted, patFilterValues, ratsbFilterValues]);

  const hideValidationFilters = useMemo(() =>
    !!appliedFormValues?.validation &&
    appliedFormValues.validation !== "2" &&
    appliedFormValues.validation !== "",
  [appliedFormValues]);

  const hideRemittedFilters = useMemo(() =>
    !!appliedFormValues?.remitted &&
    appliedFormValues.remitted !== "2" &&
    appliedFormValues.remitted !== "",
  [appliedFormValues]);

  const visibleFilters = useMemo(() =>
    FILTERS.filter((f) => {
      if (hideValidationFilters && (f.key === "validados" || f.key === "noValidados")) return false;
      if (hideRemittedFilters   && (f.key === "remesados" || f.key === "noRemesados")) return false;
      if (!isRemitted && (f.key === "remesados" || f.key === "noRemesados")) return false;
      return true;
    }),
  [hideValidationFilters, hideRemittedFilters, isRemitted]);

  // Si el filtro activo ya no es visible, vuelve a "all"
  useEffect(() => {
    if (!visibleFilters.find((f) => f.key === activeFilter)) {
      setActiveFilter("all");
    }
  }, [visibleFilters, activeFilter]);

  useEffect(() => {
    if (!arrData) return;
    if (type === "withLeave") {
      setFormatedData(
        arrData.map((pat: any) => ({
          ...pat,
          TipoDelta: checkTypePAT(pat.TipoDelta),
          Estado: checkStatusPAT(pat.Estado),
          formatted_accident_date: pat.FechaAccidente
            ? moment(new Date(pat.FechaAccidente)).format("DD/MM/YYYY")
            : "",
          formatted_leaving_date: pat.FechaBaja
            ? moment(new Date(pat.FechaBaja)).format("DD/MM/YYYY")
            : "",
        }))
      );
    } else {
      setFormatedData(
        arrData.map((item: any) => ({
          ...item,
          formated_month: MONTHS[item.Mes - 1],
        }))
      );
    }
    setArrayRemesa([]);
    setActiveFilter("all");
  }, [arrData, type]);

  // Una sola pasada para todos los contadores
  const counts = useMemo(() =>
    formatedData.reduce(
      (acc, item) => {
        acc.all++;
        if (item.Validacion === true)  acc.validados++;
        if (item.Validacion === false) acc.noValidados++;
        if (item.Remesado   === true)  acc.remesados++;
        if (item.Remesado   === false) acc.noRemesados++;
        return acc;
      },
      { all: 0, validados: 0, noValidados: 0, remesados: 0, noRemesados: 0 }
    ),
  [formatedData]);

  const filteredData = useMemo(() => {
    if (activeFilter === "all") return formatedData;
    return formatedData.filter((item) => {
      switch (activeFilter) {
        case "validados":   return item.Validacion === true;
        case "noValidados": return item.Validacion === false;
        case "remesados":   return item.Remesado === true;
        case "noRemesados": return item.Remesado === false;
        default:            return true;
      }
    });
  }, [formatedData, activeFilter]);

  // Callbacks estables — las cards no se re-renderizan al marcar un checkbox
  const handleRemit = useCallback((id: string) => {
    setArrayRemesa((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleEdit = useCallback((id: string) => {
    const routes: Record<string, string> = {
      "withLeave-pending":     "accidentes-con-baja-pendientes-de-delta",
      "withLeave-remitted":    "accidentes-con-baja-delta-remesados",
      "withoutLeave-pending":  "accidentes-sin-baja-pendientes-delta",
      "withoutLeave-remitted": "accidentes-sin-baja-delta-remesados",
    };
    const key = `${type}-${isRemitted ? "remitted" : "pending"}`;
    navigate(`/unidad-delta/${routes[key]}/${id}`);
  }, [navigate, type, isRemitted]);

  const downloadXml = (data: any, filename = "default.xml") => {
    const blob = new Blob([data], { type: "application/xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const parseXmlDate = (ds: string) =>
    new Date(`${ds.slice(4, 8)}-${ds.slice(2, 4)}-${ds.slice(0, 2)}`);

  const handleOnClickRemit = async () => {
    const exportIds = { ids: arrayRemesa };
    if (type === "withLeave") {
      await exportPat(exportIds).then((res: any) => {
        try {
          const doc   = new DOMParser().parseFromString(res, "application/xml");
          const ipf   = doc.getElementsByTagName("ipf")[0].textContent;
          const fecha = parseXmlDate(doc.getElementsByTagName("fechaaccidente")[0].textContent!)
            .toISOString().split("T")[0];
          downloadXml(res, `pre_delta_con_baja_${ipf}_${fecha}.xml`);
        } catch (e) { console.log(e); }
      });
    } else {
      await exportRatsb(exportIds).then((res: any) => {
        try {
          const doc  = new DOMParser().parseFromString(res, "application/xml");
          const cif  = doc.getElementsByTagName("cif")[0].textContent;
          const mes  = doc.getElementsByTagName("mes")[0].textContent;
          const anno = doc.getElementsByTagName("anno")[0].textContent;
          downloadXml(res, `pre_delta_sin_baja_${cif}_${anno}-${mes}.xml`);
        } catch (e) { console.log(e); }
      });
    }
  };

  const showRemesar = !isRemitted && !isImpersonation();

  return (
    <div className="container">
      <h2 className="section-title">Resultados de búsqueda</h2>
      {formatedData.length > 0 && visibleFilters.length > 1 && (
        <div className="delta-result-filters">
          {visibleFilters.map((f) => (
            <button
              key={f.key}
              className={`delta-result-filter-btn${activeFilter === f.key ? " delta-result-filter-btn--active" : ""}`}
              onClick={() => setActiveFilter(f.key)}
            >
              {f.label}
              <span className="delta-result-filter-btn__badge">{counts[f.key]}</span>
            </button>
          ))}
        </div>
      )}

      <p className="delta-result-count">
        {filteredData.length} resultado{filteredData.length !== 1 ? "s" : ""}
        {activeFilter !== "all" && ` · ${formatedData.length} totales`}
      </p>

      {filteredData.length === 0 ? (
        <NoData
          message={
            activeFilter === "all"
              ? "No se han encontrado resultados para la búsqueda."
              : "No hay resultados para el filtro seleccionado."
          }
        />
      ) : (
        <div className="delta-result-grid">
          {type === "withLeave"
            ? filteredData.map((item) => (
                <DeltaCardConBaja
                  key={item.Id}
                  item={item}
                  isRemitted={isRemitted}
                  checked={arrayRemesa.includes(item.Id)}
                  showRemesar={showRemesar}
                  onRemit={handleRemit}
                  onEdit={handleEdit}
                />
              ))
            : filteredData.map((item) => (
                <DeltaCardSinBaja
                  key={item.Id}
                  item={item}
                  isRemitted={isRemitted}
                  checked={arrayRemesa.includes(item.Id)}
                  showRemesar={showRemesar}
                  onRemit={handleRemit}
                  onEdit={handleEdit}
                />
              ))}
        </div>
      )}

      {showRemesar && (
        <div className="container container-btn-remesar">
          <button
            className="btn-remesar btn-box btn-accent loading"
            disabled={arrayRemesa.length === 0}
            onClick={handleOnClickRemit}
          >
            Remesar
          </button>
        </div>
      )}
    </div>
  );
};

export default ResultadoBusquedaDeltas;
